import { IJPlistItem } from '../interfaces';
import { IGNORE_REGEX } from '../jplist';

export const ASC = 'asc';

/**
 * general multiple sort
 * type - text|number|datetime
 * property - any item property; empty value meaning the whole element; 'default' means no change
 * order - asc or desc
 * nestedSortProperty - in case of equal items, sort them by 'nestedSortProperty'
 * ignoreRegex - optional regex that defines what characters should be ignored
 * returns sorted items
 */
export const sort = (
    items: IJPlistItem[],
    type: string,
    property: string= '',
    order: string = ASC,
    nestedSortProperty: string = '',
    ignoreRegex: string = IGNORE_REGEX) : IJPlistItem[] => {

    // no change is needed
    if(property === 'default') return items;

    items.sort((item1, item2) => {
        return sortHelper(item1, item2, type, property, order, nestedSortProperty, ignoreRegex);
    });

    return items;
};

/**
 * sort helper
 * type - text|number|datetime
 * property - any item property; empty value meaning the whole element; 'default' means no change
 * order - asc or desc
 * nestedSortProperty - in case of equal items, sort them by 'nestedSortProperty'
 * ignoreRegex - optional regex that defines what characters should be ignored
 * returns 0 if equal, <0 if item1 < item2, >0 if item1 > item2
 */
const sortHelper = (
    item1: IJPlistItem,
    item2: IJPlistItem,
    type: string,
    property: string,
    order: string,
    nestedSortProperty: string,
    ignoreRegex: string) : number => {

    switch(type){

        case 'number': {
            return sortNumbers(item1, item2, property, order, nestedSortProperty);
        }

        // case 'datetime': {
        //     return sortDateTime(item1, item2, property, order);
        // }

        default: {
            return sortText(item1, item2, property, order, nestedSortProperty, ignoreRegex);
        }
    }
}

/**
 * sort text
 * property - any item property; empty value meaning the whole element; 'default' means no change
 * order - asc or desc
 * nestedSortProperty - in case of equal items, sort them by 'nestedSortProperty'
 * ignoreRegex - optional regex that defines characters that should be ignored before the sorting
 * returns 0 if equal, <0 if item1 < item2, >0 if item1 > item2
 */
const sortText = (
    item1: IJPlistItem,
    item2: IJPlistItem,
    property: string = '',
    order: string = ASC,
    nestedSortProperty: string = '',
    ignoreRegex: string = IGNORE_REGEX) : number => {

    if(!item1 || !item2){
        return 0;
    }

    // find content to sort
    const value1 = (property === '' ) ? item1 : item1[property];
    const value2 = (property === '' ) ? item2 : item2[property];

    let text1 = value1 !== undefined ? value1.toString().trim().toLowerCase() : '';
    let text2 = value2 !== undefined ? value2.toString().trim().toLowerCase() : '';

    if(ignoreRegex){

        //regex expression that is used to remove irrelevant characters
        const regexExpr = new RegExp(ignoreRegex, 'ig');
        text1 = text1.replace(regexExpr, '').trim();
        text2 = text2.replace(regexExpr, '').trim();
    }

    if(text1 === text2){

        if(nestedSortProperty){
            return sortText(item1, item2, nestedSortProperty, order, '');
        }
        else{
            return 0;
        }
    }

    //compare languages other than English
    if(typeof ''.localeCompare === 'function'){

        if (order === ASC) {
            return text1.localeCompare(text2);
        }
        else {
            return text2.localeCompare(text1);
        }
    }
    else{
        if (order === ASC) {
            return text1 > text2 ? 1 : -1;
        }
        else {
            return text1 < text2 ? 1 : -1;
        }
    }
}

/**
 * sort numbers
 * property - any item property; empty value meaning the whole element; 'default' means no change
 * order - asc or desc
 * nestedSortProperty - in case of equal items, sort them by 'nestedSortProperty'
 * returns 0 if equal, <0 if item1 < item2, >0 if item1 > item2
 */
const sortNumbers = (
    item1: IJPlistItem,
    item2: IJPlistItem,
    property: string = '',
    order: string = ASC,
    nestedSortProperty: string = ''): number => {

    if(!item1 || !item2){
        return 0;
    }

    //find content to sort
    let number1 = (property === '' ) ? item1 : item1[property];
    let number2 = (property === '' ) ? item2 : item2[property];

    if(typeof number1 === 'string'){
        number1 = number1.trim().toLowerCase();
        number1 = parseFloat(number1.replace(/[^-0-9.]+/g,''));
    }

    if(typeof number2 === 'string'){
        number2 = number2.trim().toLowerCase();
        number2 = parseFloat(number2.replace(/[^-0-9.]+/g,''));
    }

    if(isNaN(number1) || isNaN(number2)){

        if(isNaN(number1) && isNaN(number2)){
            return 0;
        }
        else {
            return isNaN(number1) ? 1 : -1;
        }
    }

    if(number1 === number2){
        if(nestedSortProperty){
            return sortNumbers(item1, item2, nestedSortProperty, order, '');
        }
        else{
            return 0;
        }
    }

    if(order === ASC){
        return number1 - number2;
    }
    else{
        return number2 - number1;
    }
}